"use client";
import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getRegistrationSchema } from "@/schemas/registration/registration.schema";
import { hotelOwnerRegistrationDefaultValues } from "@/lib/validation/register";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Globe,
  BarChart3,
  Calendar,
  Users,
  CreditCard,
  Smartphone,
  Shield,
  CheckCircle,
  Clock,
  Zap,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { useMutationQuery } from "@/hooks/useMutationQuery";
import { useAuth } from "@/hooks/useAuth";
import { useRouter } from "next/navigation";
import { OTP_PURPOSE } from "@/config/enums/common-status.enum";
import IntlTelInput from "@/components/ui/intl-tel-input";
import LocationEditFields from "@/components/common/LocationEditFields";
import { useTranslation } from "@/hooks/useLanguage";

export default function RegisterPage() {
  const { t } = useTranslation();
  const form = useForm({
    resolver: zodResolver(getRegistrationSchema(t)),
    defaultValues: hotelOwnerRegistrationDefaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const { user, isAuthenticated, isLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (isAuthenticated) {
      router.push("/");
    }
  }, [isAuthenticated, router]);

  const [submitting, setSubmitting] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [otpResendSeconds, setOtpResendSeconds] = useState(0);

  // Location state for LocationEditFields component
  const [locationData, setLocationData] = useState({
    country: form.getValues("hotelCountry") || "Saudi Arabia",
    countryCode: form.getValues("hotelCountryCode") || "SA",
    state: form.getValues("hotelState") || "",
    stateCode: form.getValues("hotelStateCode") || "",
    city: form.getValues("hotelCity") || "",
  });

  // Phone metadata for parsing
  const [phoneMetadata, setPhoneMetadata] = useState({
    countryCode: null,
    dialCode: null,
  });

  // Ref for IntlTelInput to reset it manually
  const phoneInputRef = useRef(null);
  // Ref to block validation during reset
  const isResettingRef = useRef(false);

  const { handleApiAction, isLoading: mutationLoading } = useMutationQuery();

  // countdown for resend
  useEffect(() => {
    if (!otpSent) return;
    if (otpResendSeconds <= 0) return;
    const t = setInterval(() => {
      setOtpResendSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [otpSent, otpResendSeconds]);

  // reset OTP state if email changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "email") {
        setEmailVerified(false);
        setOtpSent(false);
        setOtpSending(false);
        setOtpVerifying(false);
        setOtpInput("");
        setOtpResendSeconds(0);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  const sendOtp = async () => {
    // Trigger Zod validation for email field before sending OTP
    const valid = await form.trigger("email");
    if (!valid) {
      return;
    }

    setOtpSending(true);
    try {
      const email = form.getValues("email");
      await handleApiAction("sendOtp", {
        email,
        purpose: OTP_PURPOSE.REGISTRATION,
      });

      setOtpSent(true);
      setOtpResendSeconds(30);
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    setOtpVerifying(true);
    try {
      const email = form.getValues("email");
      await handleApiAction("verifyToken", {
        email,
        code: otpInput,
        purpose: OTP_PURPOSE.REGISTRATION,
      });

      setEmailVerified(true);
    } finally {
      setOtpVerifying(false);
    }
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const { phone: phoneE164, ...rest } = values;
      const dialCode = phoneMetadata.dialCode || "";
      const nationalNumber = phoneE164
        ? phoneE164.replace(`+${dialCode}`, "")
        : "";
      await handleApiAction("createPropertyOwnerRegistration", {
        ...rest,
        phone: {
          e164: phoneE164,
          countryCode: (phoneMetadata.countryCode || "").toUpperCase(),
          dialCode,
          nationalNumber,
        },
      });

      // Reset form only on success
      // Set resetting flag to block validation
      isResettingRef.current = true;

      // Reset phone input manually
      if (phoneInputRef.current) {
        phoneInputRef.current.setNumber("");
      }
      // Reset phone metadata
      setPhoneMetadata({
        countryCode: null,
        dialCode: null,
      });
      // Reset location data
      setLocationData({
        country: "Saudi Arabia",
        countryCode: "SA",
        state: "",
        stateCode: "",
        city: "",
      });
      // Reset form - explicitly reset all state to ensure fresh form
      form.reset(hotelOwnerRegistrationDefaultValues, {
        keepErrors: false,
        keepTouched: false,
        keepDirty: false,
        keepIsSubmitted: false,
        keepSubmitCount: false,
      });
      // Explicitly clear phone error after reset to ensure no schema residue
      form.clearErrors("phone");
      setEmailVerified(false);
      setOtpSent(false);
      setOtpInput("");
      setOtpResendSeconds(0);

      // Clear resetting flag after reset completes (use requestAnimationFrame for better timing)
      requestAnimationFrame(() => {
        isResettingRef.current = false;
      });
    } catch (error) {
      // Error already handled by useMutationQuery hook
      console.error("Registration failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen ">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-primary text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative mx-auto max-w-7xl px-6 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              {t("registration.hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto">
              {t("registration.hero.description")}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge variant="secondary" className="text-lg px-4 py-2">
                <Globe className="w-6 h-6 me-2" />
                {t("registration.hero.badges.0.title")}
              </Badge>
              <Badge variant="secondary" className="text-lg px-4 py-2">
                <BarChart3 className="w-6 h-6 me-2" />
                {t("registration.hero.badges.1.title")}
              </Badge>
              <Badge variant="secondary" className="text-lg px-4 py-2">
                <Clock className="w-6 h-6 me-2" />
                {t("registration.hero.badges.2.title")}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* OTA Partners Section */}
      <div className="py-16 bg-background">
        <div className="mx-auto  px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              {t("registration.ota_partners.title")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("registration.ota_partners.description")}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-8 text-2xl">
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.0")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.1")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.2")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.3")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.4")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.5")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.6")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.7")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.8")}
            </p>
            <p className="font-semibold text-foreground">
              {t("registration.ota_partners.ota_partners_list.9")}
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 bg-primary">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              {t("registration.features.title")}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t("registration.features.description")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-foreground text-lg font-semibold mb-2">
                  {t("registration.features.feature_list.0.title")}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                  {t("registration.features.feature_list.0.description")}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="p-6">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-success" />
                </div>
                <CardTitle className="text-foreground text-lg font-semibold mb-2">
                  {t("registration.features.feature_list.1.title")}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                  {t("registration.features.feature_list.1.description")}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="p-6">
                <div className="w-12 h-12 bg-chart-3/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-chart-3" />
                </div>
                <CardTitle className="text-foreground text-lg font-semibold mb-2">
                  {t("registration.features.feature_list.2.title")}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                  {t("registration.features.feature_list.2.description")}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="p-6">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center mb-4">
                  <CreditCard className="w-6 h-6 text-warning" />
                </div>
                <CardTitle className="text-foreground text-lg font-semibold mb-2">
                  {t("registration.features.feature_list.3.title")}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                  {t("registration.features.feature_list.3.description")}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="p-6">
                <div className="w-12 h-12 bg-danger/10 rounded-lg flex items-center justify-center mb-4">
                  <Smartphone className="w-6 h-6 text-danger" />
                </div>
                <CardTitle className="text-foreground text-lg font-semibold mb-2">
                  {t("registration.features.feature_list.4.title")}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                  {t("registration.features.feature_list.4.description")}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="p-6">
                <div className="w-12 h-12 bg-info/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-info" />
                </div>
                <CardTitle className="text-foreground text-lg font-semibold mb-2">
                  {t("registration.features.feature_list.5.title")}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                  {t("registration.features.feature_list.5.description")}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                {t("registration.benefits.title")}
              </h2>
              <div className="space-y-4">
                {(() => {
                  const benefitsList = t("registration.benefits.benefits_list");
                  return Array.isArray(benefitsList) ? (
                    <>
                      <div className="flex items-start">
                        <CheckCircle className="w-6 h-6 text-success mt-1 me-3 shrink-0" />
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {benefitsList[0]?.title || ""}
                          </h3>
                          <p className="text-muted-foreground">
                            {benefitsList[0]?.description || ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="w-6 h-6 text-success mt-1 me-3 shrink-0" />
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {benefitsList[1]?.title || ""}
                          </h3>
                          <p className="text-muted-foreground">
                            {benefitsList[1]?.description || ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="w-6 h-6 text-success mt-1 me-3 shrink-0" />
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {benefitsList[2]?.title || ""}
                          </h3>
                          <p className="text-muted-foreground">
                            {benefitsList[2]?.description || ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircle className="w-6 h-6 text-success mt-1 me-3 shrink-0" />
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {benefitsList[3]?.title || ""}
                          </h3>
                          <p className="text-muted-foreground">
                            {benefitsList[3]?.description || ""}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : null;
                })()}
              </div>
            </div>

            <div className=" p-8 text-white">
              <div className="text-center">
                <Zap className="w-16 h-16 mx-auto mb-6 text-warning" />
                <h3 className="text-2xl font-bold mb-4 text-foreground">
                  {t("registration.benefits.stats.title")}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {t("registration.benefits.stats.description")}
                </p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  {(() => {
                    const statsList = t(
                      "registration.benefits.stats.stats_list",
                    );
                    return Array.isArray(statsList) ? (
                      <>
                        <div>
                          <div className="text-3xl font-bold text-foreground">
                            {statsList[0]?.value || ""}
                          </div>
                          <div className="text-sm text-primary-foreground/60">
                            {statsList[0]?.label || ""}
                          </div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-foreground">
                            {statsList[1]?.value || ""}
                          </div>
                          <div className="text-sm text-primary-foreground/60">
                            {statsList[1]?.label || ""}
                          </div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-foreground">
                            {statsList[2]?.value || ""}
                          </div>
                          <div className="text-sm text-primary-foreground/60">
                            {statsList[2]?.label || ""}
                          </div>
                        </div>
                      </>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Form Section */}
      <div id="registration-form" className="py-16 bg-primary">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              {t("registration.registration_form.title")}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t("registration.registration_form.description")}
            </p>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl">
                {t("registration.registration_form.card_title")}
              </CardTitle>
              <CardDescription className="text-lg">
                {t("registration.registration_form.card_description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>
                            {t(
                              "common.first_name",
                            )}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t(
                                "common.john",
                              )}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>
                            {t(
                              "common.last_name",
                            )}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t(
                                "common.doe",
                              )}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>
                            {t("common.email")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder={t(
                                "registration.registration_form.placeholders.email",
                              )}
                              disabled={emailVerified}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>
                            {t("common.phone")}
                          </FormLabel>
                          <FormControl>
                            <IntlTelInput
                              ref={phoneInputRef}
                              value={field.value}
                              onChange={(e) => {
                                const phoneValue = e.target.value || "";
                                const isOnlyDialCode =
                                  phoneValue && /^\+\d{1,4}$/.test(phoneValue);

                                // Don't update form if empty or only dial code
                                if (!phoneValue || isOnlyDialCode) {
                                  field.onChange("");
                                  form.clearErrors("phone");
                                  return;
                                }

                                field.onChange(phoneValue);

                                // Store phone metadata for backend parsing
                                if (e.target.countryCode && e.target.dialCode) {
                                  setPhoneMetadata({
                                    countryCode: e.target.countryCode,
                                    dialCode: e.target.dialCode,
                                  });
                                }

                                // Clear any previous errors when user types
                                if (phoneValue && form.formState.errors.phone) {
                                  form.clearErrors("phone");
                                }
                              }}
                              onBlur={(e) => {
                                field.onBlur();

                                // Block validation during reset
                                if (isResettingRef.current) return;

                                // Since phone is optional, only clear errors on blur
                                // Let Zod handle validation on submit
                                form.clearErrors("phone");
                              }}
                              placeholder={t(
                                "common.xx_xxx_xxxx",
                              )}
                              className="w-full"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* OTP controls in their own row so Email doesn't stretch the Email/Phone row */}
                    <div className="md:col-span-2">
                      {!emailVerified ? (
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          {!otpSent && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={otpSending}
                              onClick={sendOtp}
                            >
                              {otpSending
                                ? t(
                                    "common.sending",
                                  )
                                : t(
                                    "registration.registration_form.buttons.send_otp",
                                  )}
                            </Button>
                          )}

                          {otpSent && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <InputOTP
                                maxLength={6}
                                value={otpInput}
                                onChange={setOtpInput}
                                containerClassName=""
                              >
                                <InputOTPGroup>
                                  <InputOTPSlot index={0} />
                                  <InputOTPSlot index={1} />
                                  <InputOTPSlot index={2} />
                                  <InputOTPSlot index={3} />
                                  <InputOTPSlot index={4} />
                                  <InputOTPSlot index={5} />
                                </InputOTPGroup>
                              </InputOTP>
                              <Button
                                type="button"
                                size="sm"
                                disabled={otpVerifying || otpInput.length < 6}
                                onClick={verifyOtp}
                              >
                                {otpVerifying ? "Verifying..." : "Verify"}
                              </Button>
                              {otpResendSeconds > 0 ? (
                                <span className="text-muted-foreground text-xs">
                                  {t(
                                    "registration.registration_form.buttons.resend_in",
                                  )}{" "}
                                  {otpResendSeconds}s
                                </span>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={sendOtp}
                                >
                                  {t(
                                    "registration.registration_form.buttons.resend",
                                  )}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-success text-sm mt-2">
                          {t(
                            "registration.registration_form.messages.email_verified",
                          )}
                        </p>
                      )}
                    </div>

                    <FormField
                      control={form.control}
                      name="hotelName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>
                            {t(
                              "common.hotel_name",
                            )}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t(
                                "common.sunrise_resort",
                              )}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("common.website_optional")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t(
                                "common.https_example_com",
                              )}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Location Fields using LocationEditFields component - below phone field */}
                  <div className="space-y-4 mt-4">
                    <LocationEditFields
                      initialData={{
                        country: form.watch("hotelCountry") || "Saudi Arabia",
                        countryCode: form.watch("hotelCountryCode") || "SA",
                        state: form.watch("hotelState") || "",
                        stateCode: form.watch("hotelStateCode") || "",
                        city: form.watch("hotelCity") || "",
                      }}
                      onChange={(locationData) => {
                        // Update form values when location changes
                        form.setValue("hotelCountry", locationData.country);
                        form.setValue(
                          "hotelCountryCode",
                          locationData.countryCode,
                        );
                        form.setValue("hotelState", locationData.state);
                        form.setValue("hotelStateCode", locationData.stateCode);
                        form.setValue("hotelCity", locationData.city);
                        // Generate city code if not provided
                        if (locationData.city && !locationData.cityCode) {
                          const cityCode = locationData.city
                            .toUpperCase()
                            .replace(/\s+/g, "_")
                            .substring(0, 10);
                          form.setValue("hotelCityCode", cityCode);
                        } else if (locationData.cityCode) {
                          form.setValue("hotelCityCode", locationData.cityCode);
                        }
                        // Update local state
                        setLocationData(locationData);
                      }}
                    />
                    {/* Show form errors for location fields */}
                    <div className="space-y-1">
                      {form.formState.errors.hotelCountry && (
                        <p className="text-sm font-medium text-destructive">
                          {form.formState.errors.hotelCountry.message}
                        </p>
                      )}
                      {form.formState.errors.hotelState && (
                        <p className="text-sm font-medium text-destructive">
                          {form.formState.errors.hotelState.message}
                        </p>
                      )}
                      {form.formState.errors.hotelCity && (
                        <p className="text-sm font-medium text-destructive">
                          {form.formState.errors.hotelCity.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Hidden fields for codes - required by backend */}
                  <FormField
                    control={form.control}
                    name="hotelCountryCode"
                    render={({ field }) => <input type="hidden" {...field} />}
                  />
                  <FormField
                    control={form.control}
                    name="hotelStateCode"
                    render={({ field }) => <input type="hidden" {...field} />}
                  />
                  <FormField
                    control={form.control}
                    name="hotelCityCode"
                    render={({ field }) => <input type="hidden" {...field} />}
                  />
                  <FormField
                    control={form.control}
                    name="hotelState"
                    render={({ field }) => {
                      // If hotelState is not set but hotelCity is, use hotelCity as fallback
                      if (!field.value && form.getValues("hotelCity")) {
                        field.value = form.getValues("hotelCity");
                      }
                      return <input type="hidden" {...field} />;
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("registration.registration_form.fields.message")}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            rows={4}
                            placeholder={t(
                              "common.number_of_rooms_current_challenges_specific",
                            )}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex items-center justify-center gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={submitting || !emailVerified}
                      size="lg"
                      className="px-8 py-3 text-lg"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
                          {t(
                            "common.submitting",
                          )}
                        </>
                      ) : (
                        t("common.register")
                      )}
                    </Button>
                  </div>

                  <p className="text-center text-sm text-muted-foreground">
                    {t("registration.registration_form.messages.footer_text")}
                  </p>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer CTA */}
      <div className=" bg-background py-12">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-2xl font-bold mb-4">
            {t("registration.footer_cta.title")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t("registration.footer_cta.description")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg" className="px-6">
              {t("registration.footer_cta.buttons.schedule_demo")}
            </Button>
            <Button variant="secondary" size="lg" className="px-6">
              {t("registration.footer_cta.buttons.call")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
